import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PhilDropdownComponent } from '../../../common-components/phil-dropdown/phil-dropdown.component';
import { PhilInputComponent } from '../../../common-components/phil-input/phil-input.component';
import { EMAIL_PATTERN, PHONE_LENGTH, PHONE_PATTERN, findCountry } from '../sso-login.models';

type LocationIssue = 'prime' | 'blocked' | 'deviceOff' | 'generic';

const LOCATION_BACKEND_ERROR = /deviceinfo|latitude|longitude|\blat\b|\blng\b|geolocation/i;
const FRIENDLY_LOCATION_MESSAGE = 'Location is needed to log in. Please turn it on and try again.';
const SELECT_COUNTRY_MESSAGE = 'Please select your country/region first';

@Component({
  selector: 'app-contact-step',
  templateUrl: './contact-step.component.html',
  styleUrls: ['./contact-step.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PhilDropdownComponent, PhilInputComponent],
})
export class ContactStepComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) selectedCountry!: FormControl<string | null>;
  @Input() countryList: { id: string; title: string }[] = [];
  @Input() isLoading = false;
  @Input() errorText = '';
  @Input() buttonText = 'Login';
  @Input() buttonDisabled = true;

  @Output() submitted = new EventEmitter<void>();
  @Output() countrySelected = new EventEmitter<string>();
  @Output() locationCaptured = new EventEmitter<{ lat: number; lng: number }>();

  @ViewChild('locationDialog', { static: true })
  locationDialog!: ElementRef<HTMLDialogElement>;

  isLocating = false;
  locationIssue: LocationIssue = 'prime';
  locationHint = '';

  private permissionStatus: PermissionStatus | null = null;
  private closedBySuccess = false;
  private retriedAfterBackendError = false;
  private subscriptions = new Subscription();

  constructor(private zone: NgZone) {}

  get contactControl(): FormControl {
    return this.form.get('contact') as FormControl;
  }

  get isEmailMode(): boolean {
    return findCountry(this.selectedCountry?.value)?.contactType === 'email';
  }

  get hasCountry(): boolean {
    return !!this.selectedCountry?.value;
  }

  get contactLabel(): string {
    if (!this.hasCountry) {
      return 'Email or phone number';
    }
    return this.isEmailMode ? 'Email' : 'Phone number';
  }

  get contactPlaceholder(): string {
    if (!this.hasCountry) {
      return 'Select country/region first';
    }
    return this.isEmailMode
      ? 'Enter your email address'
      : `Enter ${PHONE_LENGTH} digit phone number`;
  }

  get contactInputType(): string {
    if (!this.hasCountry) {
      return 'text';
    }
    return this.isEmailMode ? 'email' : 'tel';
  }

  get contactError(): string {
    const control = this.contactControl;
    if (!control || !control.errors || !(control.touched || control.dirty)) {
      return '';
    }
    if (!this.hasCountry) {
      return SELECT_COUNTRY_MESSAGE;
    }
    if (control.errors['required']) {
      return this.isEmailMode ? 'Email is required' : 'Phone number is required';
    }
    return this.isEmailMode
      ? 'Enter a valid email address'
      : `Enter a valid ${PHONE_LENGTH} digit phone number`;
  }

  get displayError(): string {
    if (this.errorText) {
      return LOCATION_BACKEND_ERROR.test(this.errorText)
        ? FRIENDLY_LOCATION_MESSAGE
        : this.errorText;
    }
    if (this.contactError) {
      return this.contactError;
    }
    return this.locationHint;
  }

  get primaryButtonLabel(): string {
    if (this.locationIssue === 'prime') {
      return 'Continue';
    }
    if (this.locationIssue === 'generic') {
      return 'Try again';
    }
    return 'I have turned it on';
  }

  ngOnInit(): void {
    this.applyContactValidators();

    this.subscriptions.add(
      this.selectedCountry.valueChanges.subscribe(() => {
        this.contactControl?.setValue('', { emitEvent: true });
        this.contactControl?.markAsPristine();
        this.contactControl?.markAsUntouched();
        this.applyContactValidators();
      })
    );

    this.subscriptions.add(
      this.contactControl.valueChanges.subscribe((value: string | null) => {
        if (!this.hasCountry || this.isEmailMode) {
          return;
        }
        const raw = value ?? '';
        const cleaned = raw.replace(/\D/g, '').slice(0, PHONE_LENGTH);
        if (cleaned !== raw) {
          this.contactControl.setValue(cleaned, { emitEvent: true });
        }
      })
    );

    void this.watchPermission();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['errorText'];
    if (change && !change.firstChange && this.errorText && LOCATION_BACKEND_ERROR.test(this.errorText)) {
      void this.routeBackendLocationError();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.permissionStatus) {
      this.permissionStatus.onchange = null;
    }
  }

  onFormSubmit(): void {
    if (this.isLoading || this.isLocating) {
      return;
    }
    this.contactControl?.markAsTouched();
    if (this.buttonDisabled) {
      return;
    }
    this.retriedAfterBackendError = false;
    if (!('geolocation' in navigator)) {
      this.submitted.emit();
      return;
    }
    void this.startLocationFlow();
  }

  requestLocation(): void {
    if (this.isLocating) {
      return;
    }
    this.isLocating = true;
    this.locationHint = '';
    navigator.geolocation.getCurrentPosition(
      position => {
        this.zone.run(() => {
          this.isLocating = false;
          const dialog = this.locationDialog?.nativeElement;
          if (dialog?.open) {
            this.closedBySuccess = true;
            dialog.close();
          }
          this.locationCaptured.emit({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          this.submitted.emit();
        });
      },
      error => {
        this.zone.run(() => {
          this.isLocating = false;
          if (error.code === error.PERMISSION_DENIED) {
            this.openLocationPopup(this.permissionStatus?.state === 'prompt' ? 'prime' : 'blocked');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            this.openLocationPopup('deviceOff');
          } else {
            this.openLocationPopup('generic');
          }
        });
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  }

  closeLocationPopup(): void {
    if (this.isLocating) {
      return;
    }
    const dialog = this.locationDialog?.nativeElement;
    if (dialog?.open) {
      dialog.close();
    }
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.locationDialog.nativeElement) {
      this.closeLocationPopup();
    }
  }

  onDialogCancel(event: Event): void {
    if (this.isLocating) {
      event.preventDefault();
    }
  }

  onDialogClosed(): void {
    if (!this.closedBySuccess) {
      this.locationHint = FRIENDLY_LOCATION_MESSAGE;
    }
    this.closedBySuccess = false;
  }

  private applyContactValidators(): void {
    const control = this.contactControl;
    if (!control) {
      return;
    }

    const validators: ValidatorFn[] = [Validators.required];

    if (this.hasCountry) {
      if (this.isEmailMode) {
        validators.push(Validators.pattern(EMAIL_PATTERN));
      } else {
        validators.push(Validators.pattern(PHONE_PATTERN));
      }
    }

    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private async startLocationFlow(): Promise<void> {
    const state = await this.getPermissionState();
    if (state === 'denied') {
      this.openLocationPopup('blocked');
      return;
    }
    if (state === 'prompt') {
      this.openLocationPopup('prime');
      return;
    }
    this.requestLocation();
  }

  private async routeBackendLocationError(): Promise<void> {
    const state = await this.getPermissionState();
    if (state === 'granted') {
      if (!this.retriedAfterBackendError) {
        this.retriedAfterBackendError = true;
        this.requestLocation();
      }
      return;
    }
    this.openLocationPopup(state === 'denied' ? 'blocked' : 'prime');
  }

  private async watchPermission(): Promise<void> {
    if (!('permissions' in navigator)) {
      return;
    }
    try {
      this.permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      this.permissionStatus.onchange = () => {
        this.zone.run(() => {
          if (this.permissionStatus?.state === 'granted' && this.isPopupOpen()) {
            this.requestLocation();
          }
        });
      };
    } catch {
      this.permissionStatus = null;
    }
  }

  private async getPermissionState(): Promise<PermissionState | 'unknown'> {
    if (!this.permissionStatus) {
      await this.watchPermission();
    }
    return this.permissionStatus ? this.permissionStatus.state : 'unknown';
  }

  private openLocationPopup(issue: LocationIssue): void {
    this.locationIssue = issue;
    const dialog = this.locationDialog?.nativeElement;
    if (dialog && !dialog.open) {
      dialog.showModal();
      if (issue === 'prime') {
        setTimeout(() => {
          if (this.isPopupOpen() && this.locationIssue === 'prime' && !this.isLocating) {
            this.requestLocation();
          }
        }, 600);
      }
    }
  }

  private isPopupOpen(): boolean {
    return !!this.locationDialog?.nativeElement.open;
  }
}